import type { TreeData } from "@/utils/tree-generation/tree-data";
import { assert, GUID } from "@faro-lotv/foundation";
import { useCallback, useMemo, type JSX } from "react";
import type { RowRendererProps } from "react-arborist";
import { useSelectNodeCallback } from "./capture-tree-hooks";
import { useCaptureTreeSelectionStore } from "./capture-tree-selection-store";
import { TreeRow } from "../tree-row";

/**
 * This component is used to re-implement multi-selection functionalities of the capture tree view.
 * React-arborist provides multi-selection out of the box, but there is a known bug in that library
 * and the fixing PR is not getting merged. Please see https://faro01.atlassian.net/browse/NRT-2161
 * for more information.
 *
 * @returns A component used to override the selection functionalities of the capture tree view
 */
export function CaptureTreeRow({
  node,
  innerRef,
  attrs,
  children,
}: RowRendererProps<TreeData>): JSX.Element {
  const nodeChildren = useMemo(() => {
    if (node.children?.length) {
      const childrenIds = new Array<GUID>();
      const stack = [node];
      while (stack.length) {
        const current = stack.pop();
        assert(
          current,
          "Top element of nonempty stack should always be defined",
        );
        childrenIds.push(current.data.id);
        if (current.children?.length) {
          stack.push(...current.children);
        }
      }
      return childrenIds;
    }
    return [];
  }, [node]);

  const { setHoveredId, setHoveredIds } = useCaptureTreeSelectionStore();

  const onPointerEntered = useCallback(() => {
    if (nodeChildren.length > 0) {
      setHoveredIds(nodeChildren);
    } else {
      setHoveredId(node.id);
    }
  }, [node.id, setHoveredId, setHoveredIds, nodeChildren]);

  const onPointerExited = useCallback(() => {
    setHoveredId(null);
  }, [setHoveredId]);

  const selectNode = useSelectNodeCallback();
  const onClick = useCallback(() => {
    selectNode([node]);
  }, [node, selectNode]);

  return (
    <TreeRow
      node={node}
      innerRef={innerRef}
      attrs={attrs}
      onClick={onClick}
      onPointerEnter={onPointerEntered}
      onPointerLeave={onPointerExited}
    >
      {children}
    </TreeRow>
  );
}
